export interface WaveformData {
  step: number;
  version: number;
  data: number[][];
}

// ============================================================================
// Audio Engine Types
// ============================================================================

/**
 * Track info for creating a track (metadata only, no regions)
 */
export interface TrackInfo {
  id: string;
  label: string;
  order: number;
}

/**
 * Region data for a single audio region.
 * Note: duration is optional for backwards compatibility with AudioPlayer,
 * but required when using AudioEngine.
 */
export interface RegionData {
  id: string;
  trackId: string;
  key: string;
  waveformKey: string | null;
  position: number;
  duration?: number;
  channelCount?: number | null;
  codec: string;
  /** Pre-loaded audio blob. When provided, skips downloading from storage (still decodes locally). */
  audioBlob?: Blob;
  /** Pre-computed waveform data. When provided, skips downloading/calculating waveform. */
  waveformData?: WaveformData;
}

/**
 * Per-track state (playback + metadata)
 */
export interface TrackState {
  id: string;
  label: string;
  order: number;
  solo: boolean;
  muted: boolean;
  gain: number;
  /** Computed from solo/muted + global solo state */
  disabled: boolean;
}

/**
 * Region state for a single audio region within a track
 */
export interface RegionState {
  id: string;
  trackId: string;
  position: number;
  duration: number;
  /** True for recorded-but-not-uploaded regions */
  pendingUpload: boolean;
}

/**
 * High-frequency state updated on animation frames.
 * Separated from main state to avoid unnecessary re-renders in components
 * that don't need timestamp/level updates (~60 times/sec during playback).
 */
export interface AudioEngineTickState {
  currentTimestamp: number;
  inputLevel: number;
  inputLevelRight: number;
  recordingWaveform: WaveformData | null;
}

/**
 * Full state exposed by AudioEngine
 */
export interface AudioEngineState {
  /** Whether the engine has loaded and is ready to play */
  ready: boolean;

  /** Transport state */
  playing: boolean;
  seeking: boolean;
  duration: number;

  /** Master gain (0-1) */
  masterGain: number;

  /** Per-track state keyed by track ID */
  tracks: Record<string, TrackState>;

  /** Per-region state keyed by region ID */
  regions: Record<string, RegionState>;

  /** Waveform data keyed by region ID */
  waveforms: Record<string, WaveformData>;

  /** Recording state */
  recording: boolean;
  recordingStartPosition: number | null;

  /** Selected input device ID for recording */
  selectedInputDeviceId: string | null;

  /** Input level monitoring */
  inputMonitoring: boolean;

  /** Input audio monitoring (hear yourself through speakers) */
  inputAudioMonitoring: boolean;

  /** Input channel configuration */
  inputChannelCount: number | null;
  selectedInputChannel: 'left' | 'right' | 'stereo';

  /** Recording target track ID (always set when recording) */
  recordingTargetTrackId: string | null;

  /** Region IDs that were deleted by punch-in (need to be deleted from DB on save) */
  deletedRegionIds: string[];

  /** Playback speed (1.0 = normal, 0.5 = half speed, 2.0 = double speed) */
  playbackRate: number;

  /** Transpose offset in semitones (0 = no transpose, positive = up, negative = down) */
  transpose: number;

  /** Loading progress for audio download */
  loadingProgress: LoadingProgress;
}

/**
 * Persisted data for a pending region (recorded but not uploaded)
 */
export interface PersistedPendingRegion {
  id: string;
  trackId: string;
  position: number;
  duration: number;
}

/**
 * Loading progress state for tracking audio download progress
 */
export interface LoadingProgress {
  /** Total number of regions to load */
  total: number;
  /** Number of regions loaded so far */
  loaded: number;
  /** Error if loading failed, null otherwise */
  error: Error | null;
}

/**
 * Subset of state that gets persisted to storage
 */
export interface PersistedEngineState {
  masterGain: number;
  tracks: Record<string, {
    solo: boolean;
    muted: boolean;
    gain: number;
  }>;
  /** Selected input device ID for recording */
  selectedInputDeviceId?: string | null;
  /** Selected input channel configuration */
  selectedInputChannel?: 'left' | 'right' | 'stereo';
  /** Input audio monitoring (hear yourself through speakers) */
  inputAudioMonitoring?: boolean;
  /** Pending regions that haven't been uploaded yet */
  pendingRegions?: PersistedPendingRegion[];
  /** Region IDs deleted by punch-in that need to be removed from database */
  deletedRegionIds?: string[];
  /** Waveform data for pending regions */
  pendingWaveforms?: Record<string, WaveformData>;
}

/**
 * Options for loading audio into the engine
 */
export interface AudioEngineLoadOptions {
  audioContext: AudioContext;
  tracks: TrackInfo[];
  regions: RegionData[];
  duration: number;
  songId?: string; // For persistence
  downloadKey: (key: string) => Promise<Blob>;
  streamingAudioEngineEnabled?: boolean;
}

/**
 * Default gain value for tracks.
 */
export const DEFAULT_GAIN = 0.8;

/**
 * Default track state values (without id, label, order which are required)
 */
export const DEFAULT_TRACK_STATE_VALUES = {
  solo: false,
  muted: false,
  gain: DEFAULT_GAIN,
  disabled: false,
} as const;

// ============================================================================
// Recording Types
// ============================================================================

/**
 * Audio input device information
 */
export interface AudioInputDevice {
  deviceId: string;
  label: string;
}

/**
 * Data returned when recording stops
 */
export interface PendingRecording {
  id: string;
  blob: Blob;
  position: number;
  duration: number;
  /** Punch-in specific data (only present when recording was a punch-in) */
  punchIn?: PunchInResult;
}

/**
 * Result of a punch-in recording, including info about affected regions
 */
export interface PunchInResult {
  /** Track ID that was punched into */
  trackId: string;
  /** IDs of original regions that were modified (should be deleted on persist) */
  deletedRegionIds: string[];
  /** New regions created from splitting/trimming (need to be persisted) */
  createdRegions: CreatedRegion[];
}

/**
 * A region created during punch-in (from splitting/trimming existing regions)
 */
export interface CreatedRegion {
  id: string;
  position: number;
  duration: number;
  /** The audio buffer for this region (for upload) */
  audioBuffer: AudioBuffer;
}

// ============================================================================
// Audio Input Helpers
// ============================================================================

/**
 * Creates audio constraints for stereo input with audio processing disabled.
 *
 * This is used for both input level monitoring and recording to ensure:
 * - Stereo tracks are captured separately (not mixed to mono)
 * - Browser audio processing doesn't interfere with per-channel monitoring
 *
 * @param deviceId - The device ID to use
 * @returns MediaTrackConstraints for getUserMedia
 */
export function createStereoInputConstraints(deviceId: string): MediaTrackConstraints {
  return {
    deviceId: { exact: deviceId },
    channelCount: { ideal: 2 },
    // Disable audio processing that can mix/duplicate tracks
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
  };
}
